import { formatAppDate } from '../utility/dateLocale';
import { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Group,
  Title,
  Text,
  Badge,
  ActionIcon,
  Stack,
  Textarea,
  ScrollArea,
  Table
} from '@mantine/core';
import { DateInput } from "@mantine/dates";
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import { mergeDefinedFields, presentRowKeys } from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import { readApiErrorMessage } from '../utility/apiErrors';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { IconPlus } from "@tabler/icons-react";
import { PhoneInput, PhoneInputValue } from '../components/PhoneInput';
import { Options } from '../utility/types';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { useTranslation } from 'react-i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';

import { ClientContact, initialClientContactForm, ClientContactModal } from '../components/ClientContactsModal';

type Contact = {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  date_of_birth?: string;
  contact_date: string;
  position: string;
  notes: string;
  clients: ClientContact[];
  campaign_id?: string;
};

type ContactForm = Omit<Contact, 'id' | 'uuid'>;

type ClientInfo = {
  id: number;
  legal_name: string;
};

const initialContactForm: ContactForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  contact_date: new Date(Date.now()).toISOString().split('T')[0],
  position: '',
  notes: '',
  clients: [],
};

export default function ContactsPage() {
  const { accessToken, actualEnterprise } = useAuth();
  const { t, i18n } = useTranslation();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const [contacts, setContacts] = useState<Contact[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingClient, setEditingClient] = useState<Contact | null>(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [contactForm, setContactForm] = useState<ContactForm>(initialContactForm);
  const [errorMessage, setErrorMessage] = useState('');
  // const [emailError, setEmailError] = useState('');

  const [clients, setClients] = useState<Set<string>>(new Set());
  const [campaigns, setCampaigns] = useState<Options[]>([]);

  const [clientContactModalOpened, setClientContactModalOpened] = useState(false);
  const [isEditingClientContact, setIsEditingClientContact] = useState(false);
  const [editingIndexClientContact, setEditingIndexClientContact] = useState<number>(0);
  const [initialClientContact, setInitialClientContact] = useState<ClientContact | null>(null);

  // Estado para armazenar o arquivo importado via dropzone
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);

  const nameLength = 50;
  const positionLength = 200;
  const notesLength = 1000;
  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    document.title = t('contacts.pageTitle');
  }, [t, i18n.language]);

  const saveClientContact = (
    clientContact: ClientContact,
    isEditingClientContact: boolean,
    idx: number
  ) => {
    setContactForm((prev) => ({
      ...prev,
      clients: isEditingClientContact
        ? prev.clients.map((c, i) => (i === idx ? clientContact : c))
        : [...prev.clients, clientContact],
    }));
    setClientContactModalOpened(false);
  };

  const deleteClientContact = (index: number) => {
    setClientContactModalOpened(false);
    setContactForm((prev) => ({
      ...prev,
      clients: prev.clients.filter((_, i) => i !== index),
    }));
  };

  const getClients = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/clients/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedClients: ClientInfo[] = data.map((client: any) => ({
          legal_name: client.legal_name,
        }));

        setClients(
          new Set(
            formattedClients
              .map((client) => client.legal_name)
              .filter((legal_name) => legal_name !== null && legal_name !== '')
          )
        );
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err));
  };

  const isFormValid = () => {
    // const baseValid =
    //   contactForm.first_name.trim() !== '' &&
    //   contactForm.last_name.trim() !== '' &&
    //   contactForm.email.trim() !== '' &&
    //   contactForm.position.trim() !== ''
    //
    //   const clientsValid =
    //   // Sem clients ou array vazio → válido
    //   !contactForm.clients ||
    //   contactForm.clients.length === 0 ||
    //   // Se existir, todos devem ter client_id preenchido
    //   contactForm.clients.every(
    //     (c) => (c.legal_name?.trim() ?? '') !== '' && (c.email?.trim() ?? '') !== ''
    //   );
    //
    // return baseValid && clientsValid;
    return true
  };

  const openModal = (client?: Contact) => {
    if (client) {
      setEditingClient(client);
      setContactForm({
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        phone_number: client.phone_number,
        date_of_birth: client.date_of_birth,
        contact_date: client.contact_date,
        position: client.position,
        notes: client.notes,
        clients: client.clients,
      });
    } else {
      setEditingClient(null);
      setContactForm(initialContactForm);
    }
    setIsEditingClient(false);
    setModalOpened(true);
    setAutoImportFile(null);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
  };

  const handleEditClientContact = (index: number) => {
    const clientContact = contactForm.clients[index];
    setInitialClientContact(clientContact);
    setIsEditingClientContact(true);
    setEditingIndexClientContact(index);
    setClientContactModalOpened(true);
  };

  const getContacts = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/all_contacts/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedContacts: Contact[] = data.map((contact: any, index: number) => ({
          id: index + 1,
          uuid: contact.id,
          first_name: contact.first_name,
          last_name: contact.last_name || '',
          email: contact.email ?? '',
          phone_number: contact.phone_number ?? '',
          date_of_birth: new Date(contact.date_of_birth).toISOString().split('T')[0],
          contact_date: new Date(contact.contact_date).toISOString().split('T')[0],
          position: contact.position,
          notes: contact.notes,
          campaign_id: contact.campaign_id || '',
          clients:
            contact.clients !== null
              ? contact.clients.map((clientContact: any, index: number) => ({
                id: index + 1,
                contact_id: clientContact.contact_id,
                client_id: clientContact.client_id,
                email: clientContact.email ?? '',
                phone_number: clientContact.phone_number ?? '',
                legal_name: clientContact.legal_name,
              }))
              : [],
        }));

        setContacts(formattedContacts);
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err));
  };

  const deleteContact = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/contacts/${editingClient?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setModalOpened(false);
        getContacts();
      });
  };

  const updateContact = () => {
    // console.log("Updating:", JSON.stringify(contactForm));
    console.log("Updating:", contactForm);
    fetch(`${Environment.VITE_API_URL}/api/v1/contacts/${editingClient?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatContact(contactForm)),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setModalOpened(false);
        getContacts();
      });
  };

  const createContact = async () => {
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/new_contacts/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatContact(contactForm)),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('common.saveFailed'));
        setNotification({ message, color: 'red' });
        return;
      }

      setModalOpened(false);
      getContacts();
    } catch (error) {
      console.error(error);
      setNotification({ message: t('common.saveFailed'), color: 'red' });
    }
  };

  const formatContact = (contact: ContactForm) => {
    return {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone_number: contact.phone_number,
      date_of_birth: contact.date_of_birth,
      contact_date: contact.contact_date,
      position: contact.position,
      notes: contact.notes,
      clients: contact.clients,
      campaign_id: contact.campaign_id !== '' ? contact.campaign_id : null, // if contact.campaign_id !== '' else null
    };
  };

  useEffect(() => {
    getClients();
    getCampaigns();
    getContacts();
    getCampaigns();
  }, [accessToken, actualEnterprise]);


  const getCampaigns = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/all_campaigns/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const campaigns: Options[] = data.map((campaign: any) => ({
          value: campaign.id,
          label: campaign.name
        }));
        console.log('Formatted Campaigns:', campaigns);
        setCampaigns(campaigns.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  };


  const parseExcelDate = (value: unknown): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string' && dateRegex.test(value)) {
      return value;
    }
    const parsed = new Date(value as string | number);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toISOString().split('T')[0];
  };

  const rowToContactForm = (row: Record<string, unknown>): ContactForm => ({
    first_name: String(row['Primeiro Nome'] ?? row['First Name'] ?? row[t('contacts.columns.firstName')] ?? ''),
    last_name: String(row['Sobrenome'] ?? row['Surname'] ?? row[t('contacts.columns.surname')] ?? ''),
    email: String(row['E-mail'] ?? row['Email'] ?? row[t('contacts.columns.email')] ?? ''),
    phone_number: String(row['Telefone'] ?? row['Phone'] ?? row[t('contacts.columns.phoneNumber')] ?? ''),
    date_of_birth: parseExcelDate(row['Data de Nascimento'] ?? row['Birthdate'] ?? row['Date of Birth']),
    contact_date: parseExcelDate(row['Data de Contato'] ?? row['Contact Date'] ?? row[t('contacts.columns.contactDate')]),
    position: String(row['Posição'] ?? row['Position'] ?? row[t('contacts.columns.position')] ?? ''),
    notes: String(row['Notas'] ?? row['Notes'] ?? row[t('contacts.columns.notes')] ?? ''),
    clients: [],
  });

  const validateContactForm = (contact: ContactForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!contact.first_name) rowErrors.push(t('contacts.validation.firstNameRequired'));
    if (!contact.last_name) rowErrors.push(t('contacts.validation.lastNameRequired'));
    if (!contact.email) rowErrors.push(t('contacts.validation.emailRequired'));
    if (!contact.phone_number) rowErrors.push(t('contacts.validation.phoneRequired'));
    if (!contact.date_of_birth) rowErrors.push(t('contacts.validation.birthdateRequired'));
    if (!contact.contact_date) rowErrors.push(t('contacts.validation.contactDateRequired'));
    if (contact.contact_date && !dateRegex.test(contact.contact_date)) {
      rowErrors.push(t('contacts.validation.contactDateFormat'));
    }
    if (!contact.position) rowErrors.push(t('contacts.validation.positionRequired'));
    if (rowErrors.length > 0) {
      return [t('common.import.rowError', { line, errors: rowErrors.join(' ') })];
    }
    return [];
  };

  const executeContactImport = async (
    createForms: ContactForm[],
    updateEntries: Array<{ id: string; form: ContactForm }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createForms.map(formatContact),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: formatContact(entry.form),
      })),
      deleteIds,
      bulkCreateUrl: `${Environment.VITE_API_URL}/api/v1/contacts_bulk/${actualEnterprise?.id}`,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/contacts/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/contacts/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    getContacts();
  };

  const handleAutoImport = async () => {
    if (!autoImportFile) {
      return;
    }
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(contacts.map((contact) => contact.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const createForms: ContactForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const contact = rowToContactForm(row);
        errors.push(...validateContactForm(contact, line));
        createForms.push(contact);
      }

      const updateEntries: Array<{ id: string; form: ContactForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = contacts.find((contact) => contact.uuid === entry.id);
        if (!existing) {
          continue;
        }
        const patch = rowToContactForm(entry.row);
        const merged = mergeDefinedFields(
          {
            first_name: existing.first_name,
            last_name: existing.last_name,
            email: existing.email,
            phone_number: existing.phone_number,
            date_of_birth: existing.date_of_birth,
            contact_date: existing.contact_date,
            position: existing.position,
            notes: existing.notes,
            clients: existing.clients,
            campaign_id: existing.campaign_id,
          },
          patch,
          presentRowKeys(entry.row),
        );
        errors.push(...validateContactForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeContactImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const columns = useMemo<MRT_ColumnDef<Contact>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'first_name',
        header: t('contacts.columns.firstName'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'last_name',
        header: t('contacts.columns.surname'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'email',
        header: t('contacts.columns.email'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'phone_number',
        header: t('contacts.columns.phoneNumber'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'contact_date',
        Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
        header: t('contacts.columns.contactDate'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'position',
        header: t('contacts.columns.position'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'notes',
        header: t('contacts.columns.notes'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'customer',
        header: t('contacts.columns.clients'),
        accessorFn: (row) =>
          row.clients?.map((c) => c.legal_name).join(', ') ?? '',
        enableColumnFilter: true,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ row }) => {
          const clients = row.original.clients;
          return (
            <div>
              {clients !== null && clients !== undefined ? (
                clients.map((cust) => (
                  <Badge key={cust.id} variant="filled" mr="xs">
                    {cust.legal_name}
                  </Badge>
                ))
              ) : (
                <span>{t('contacts.noClients')}</span>
              )}
            </div>
          );
        },
      },
    ],
      ['first_name', 'last_name', 'email', 'position', 'notes', 'customer'],
    ),
    [t, i18n.language]
  );

  // Função para adicionar um novo Customer ao Contact
  const addClientContact = () => {
    setIsEditingClientContact(false);
    setEditingIndexClientContact(0);
    setClientContactModalOpened(true);
    setInitialClientContact({ ...initialClientContactForm });
  };

  const handleRowDoubleClick = (rowData: Contact) => {
    setModalOpened(true);
    setEditingClient(rowData);
    setContactForm(rowData);
    setIsEditingClient(true);
    setErrorMessage('');
    console.log('Dados da linha clicada:', rowData);
  };

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
  };

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: contacts,
      localization: buildMrtLocalization(t),
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: {
        density: 'sm' as MRT_DensityState,
        showGlobalFilter: true,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Contact> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'contacts',
      sheetName: 'Contacts',
      getCellValue: (row, columnId) => {
        if (columnId === 'customer') {
          return row.clients?.map((client) => client.legal_name).join(', ') ?? '';
        }
        return row[columnId as keyof Contact];
      },
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={notification}
    />
  );

  return (
    <>
      {/* Cabeçalho com botão */}
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('contacts.title')}</Title>
        <Group>
          <Button onClick={() => openModal()}>{t('contacts.addContact')}</Button>
        </Group>
      </Group>

      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="contacts"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      {/* Modal para adicionar/editar ou importar clientes */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          editingClient
            ? t('contacts.modal.editContact')
            : t('contacts.modal.addNewContact')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                {/* -- Campos Obrigatórios -- */}
                <Group grow mb="sm" >
                  <Stack>
                    <TextInput
                      label={t('contacts.form.firstName')}
                      placeholder={t('contacts.form.firstNamePlaceholder')}
                      value={contactForm.first_name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, first_name: e.target.value })
                      }
                      mb="sm"
                      // required
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${contactForm.first_name.length > nameLength ? "error" : ""}`}>
                      {t('common.characters', { count: contactForm.first_name.length, max: nameLength })}
                    </Text>
                  </Stack>
                  <Stack>
                    <TextInput
                      label={t('contacts.form.surname')}
                      placeholder={t('contacts.form.surnamePlaceholder')}
                      value={contactForm.last_name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, last_name: e.target.value })
                      }
                      mb="sm"
                      // required
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${contactForm.last_name.length > nameLength ? "error" : ""}`}>
                      {t('common.characters', { count: contactForm.last_name.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>
                <Group grow mb="sm">
                  <Stack>
                    <TextInput
                      label={t('contacts.form.email')}
                      placeholder={t('contacts.form.emailPlaceholder')}
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, email: e.target.value })
                      }
                      mb="sm"
                      type="email"
                      // required
                      maxLength={nameLength}
                    // onBlur={() => {
                    //   if (!emailRegex.test(contactForm.email)) {
                    //     setEmailError("Por favor, insira um email válido.");
                    //   }
                    // }}
                    // error = {emailError}
                    />
                    <Text className={`text-input-counter ${contactForm.email.length > nameLength ? "error" : ""}`}>
                      {t('common.characters', { count: contactForm.email.length, max: nameLength })}
                    </Text>
                  </Stack>
                  <Stack>
                    <PhoneInput
                      value={{
                        country: contactForm.phone_number.split(' ')[0] || '+55',
                        phone: contactForm.phone_number.split(' ').slice(1).join(' ') || '',
                      }}
                      onChange={(value: PhoneInputValue) =>
                        setContactForm({
                          ...contactForm,
                          phone_number: `${value.country} ${value.phone}`,
                        })
                      }
                    />
                    <Text
                      className={`text-input-counter ${contactForm.phone_number.length > nameLength ? 'error' : ''
                        }`}
                      style={{ marginTop: -12 }}      // <<< aqui você ajusta em px
                    >
                      {t('common.characters', { count: contactForm.phone_number.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>
                <Group grow mb="sm">
                  <DateInput
                    label={t('contacts.form.birthdate')}
                    placeholder={t('contacts.form.birthdatePlaceholder')}
                    value={
                      contactForm.date_of_birth
                        ? new Date(`${contactForm.date_of_birth}T00:00:00`)
                        : null
                    }
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        date_of_birth: new Date(e as Date).toISOString().split('T')[0],
                      })
                    }
                    mb="sm"
                  />
                  <DateInput
                    label={t('contacts.form.contactDate')}
                    placeholder={t('contacts.form.contactDatePlaceholder')}
                    value={
                      contactForm.contact_date
                        ? new Date(`${contactForm.contact_date}T00:00:00`)
                        : null
                    }
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        contact_date: new Date(e as Date).toISOString().split('T')[0],
                      })
                    }
                    mb="sm"
                  // required
                  />
                </Group>
                <Stack>
                  <Select
                    label={t('contacts.form.campaign')}
                    placeholder={t('contacts.form.campaignPlaceholder')}
                    value={contactForm.campaign_id}
                    onChange={(value: string) =>
                      setContactForm({ ...contactForm, campaign_id: value })
                    }
                    data={[
                      // { value: '', label: 'None' }, // opção para "não selecionar"
                      ...Array.from(campaigns).map(campaign => ({
                        value: campaign.value,
                        label: campaign.label,
                      }))
                    ]}
                    mb="sm"
                    clearable
                    searchable
                  />
                  <TextInput
                    label={t('contacts.form.position')}
                    placeholder={t('contacts.form.positionPlaceholder')}
                    value={contactForm.position}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, position: e.target.value })
                    }
                    mb="sm"
                    // required
                    maxLength={positionLength}
                  />
                  <Text className={`text-input-counter ${contactForm.position.length > positionLength ? "error" : ""}`}>
                    {t('common.characters', { count: contactForm.position.length, max: positionLength })}
                  </Text>
                </Stack>
                <Stack>
                  <Textarea
                    label={t('contacts.form.notes')}
                    placeholder={t('contacts.form.notesPlaceholder')}
                    value={contactForm.notes}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, notes: e.target.value })
                    }
                    mb="sm"
                    maxLength={notesLength}
                  />
                  <Text className={`text-input-counter ${contactForm.notes.length > notesLength ? "error" : ""}`}>
                    {t('common.characters', { count: contactForm.notes.length, max: notesLength })}
                  </Text>
                </Stack>
                <Group spacing="xs" align="center" mb="xs">
                  <Text weight={600} size="md">
                    {t('contacts.linkedCustomers')}
                  </Text>
                  <ActionIcon
                    onClick={addClientContact}
                    variant="light"
                    sx={{
                      backgroundColor: 'rgba(48,113,91,0.2)',
                      width: '32px',
                      height: '32px',
                    }}
                  >
                    <IconPlus size={16} />
                  </ActionIcon>
                </Group>
                <Stack spacing="xs" mb="sm">
                  <Table highlightOnHover withBorder striped>
                    <thead>
                      <tr>
                        <th>{t('contacts.linkedTable.client')}</th>
                        <th>{t('contacts.linkedTable.email')}</th>
                        <th>{t('contacts.linkedTable.phone')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contactForm.clients.map((customer, idx) => (
                        <tr
                          key={idx}
                          onClick={() => handleEditClientContact(idx)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{customer.legal_name}</td>
                          <td>{customer.email}</td>
                          <td>{customer.phone_number}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Stack>
              </div>
            </ScrollArea>

            <Group position="right" mt="md" style={{ padding: '1rem' }}>
              {isEditingClient && (
                <Button
                  onClick={deleteContact}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                onClick={isEditingClient ? updateContact : createContact}
                disabled={!isFormValid()}
              >
                {isEditingClient ? t('common.update') : t('common.save')}
              </Button>
            </Group>

        {errorMessage && (
          <Group position="center" mt="md" color="red">
            <Text className="error-message" color="red">
              {errorMessage !== '' ? errorMessage : null}
            </Text>
          </Group>
        )}
        <ClientContactModal
          opened={clientContactModalOpened}
          onClose={() => setClientContactModalOpened(false)}
          onSave={(clientContact) => saveClientContact(clientContact, isEditingClientContact, editingIndexClientContact)}
          isEditingClientContact={isEditingClientContact}
          initialClientContact={initialClientContact || initialClientContactForm}
          clients={clients}
          editingIndex={editingIndexClientContact}
          onDelete={(index) => deleteClientContact(index)}
        />
      </Modal>

    </>
  );
}